import { parseJsonField } from './parseJsonField';

/**
 * Normalized search result with pre-parsed JSON fields.
 * Use this interface for components that consume search results.
 */
export interface NormalizedSearchResult {
  id: string;
  title: string;
  description: string;
  type: string;
  content_type: string;
  content_id: string | null;
  price: number;
  rating: number;
  location: string;
  images: string[];
  amenities: string[];
  metadata: Record<string, unknown>;
  details?: Record<string, unknown>;
}

/**
 * Normalized room type with pre-parsed JSON fields.
 * Use this interface for components that display room information.
 */
export interface NormalizedRoomType {
  id: string;
  name: string;
  description: string;
  base_price: number;
  effective_price: number;
  capacity: number;
  amenities: string[];
  images: Array<{ id: string; url: string }>;
}

/**
 * Check if a path is a valid image URL (not a placeholder or external stock photo).
 */
function isValidImagePath(path: string | null | undefined): boolean {
  if (!path || typeof path !== 'string') return false;
  return (
    !path.includes('placeholder') &&
    !path.includes('unsplash.com') &&
    (path.includes('.jpg') ||
      path.includes('.jpeg') ||
      path.includes('.png') ||
      path.includes('.webp'))
  );
}

/**
 * Process an array of raw images into validated URL strings.
 * Handles both object format {url: string} and plain string format.
 */
function processImageArray(rawImages: unknown[]): string[] {
  if (!Array.isArray(rawImages)) return [];

  const validUrls: string[] = [];

  for (const image of rawImages) {
    let url: string | null = null;

    // Object format: {id, url, thumbnail_url, ...}
    if (typeof image === 'object' && image !== null && 'url' in image) {
      const imgObj = image as { url?: string };
      if (typeof imgObj.url === 'string') {
        url = imgObj.url;
      }
    }
    // String format: direct URL
    else if (typeof image === 'string') {
      url = image;
    }

    if (url && isValidImagePath(url)) {
      validUrls.push(url);
    }
  }

  return validUrls;
}

/**
 * Normalize a raw search result from the API.
 * Parse JSON strings once at API boundary.
 *
 * Handles field variations from:
 * - result.details.images (JSON string or array)
 * - result.metadata.images (array)
 * - result.details.amenities (JSON string or array)
 * - result.metadata.amenities (array)
 *
 * @param raw - Raw search result from API
 * @returns Normalized result with pre-parsed fields
 */
export function normalizeSearchResult(raw: unknown): NormalizedSearchResult {
  const result = raw as Record<string, unknown>;
  const details = (result.details as Record<string, unknown>) || {};
  const metadata = (result.metadata as Record<string, unknown>) || {};

  // Parse images from details or metadata
  const rawDetailsImages = parseJsonField(details.images, []);
  const rawMetadataImages = Array.isArray(metadata.images)
    ? metadata.images
    : [];

  // Process images: prefer details.images, fall back to metadata.images
  const images = processImageArray(
    Array.isArray(rawDetailsImages) && rawDetailsImages.length > 0
      ? rawDetailsImages
      : rawMetadataImages
  );

  // Parse amenities from details or metadata
  const rawAmenities =
    details.amenities !== undefined
      ? parseJsonField(details.amenities, [])
      : parseJsonField(metadata.amenities, []);

  const amenities = Array.isArray(rawAmenities) ? rawAmenities : [];

  // Extract price with fallback chain
  const price =
    (result.price as number) ||
    (details.base_price as number) ||
    (metadata.base_price as number) ||
    (metadata.price_per_person as number) ||
    ((metadata.pricing as Record<string, unknown>)?.base as number) ||
    ((metadata.pricing as Record<string, unknown>)?.price_per_person as number) ||
    0;

  return {
    id: (result.id as string) || `result-${Date.now()}`,
    title:
      (result.title as string) ||
      (details.name as string) ||
      (metadata.name as string) ||
      'Unknown Item',
    description:
      (result.description as string) ||
      (details.description as string) ||
      (metadata.description as string) ||
      '',
    type: (result.type as string) || (result.content_type as string) || 'Package',
    content_type:
      (result.content_type as string) || (result.type as string) || 'Package',
    content_id:
      (result.content_id as string) ||
      (result.id as string) ||
      (metadata.content_id as string) ||
      null,
    price,
    rating:
      (result.rating as number) ||
      (details.rating as number) ||
      (metadata.rating as number) ||
      4.5,
    location:
      (result.location as string) ||
      (details.city as string) ||
      (metadata.city as string) ||
      'Unknown Location',
    images,
    amenities,
    metadata: metadata as Record<string, unknown>,
    details: Object.keys(details).length > 0 ? details : undefined,
  };
}

/**
 * Normalize an array of search results.
 * Call this once when data arrives from API.
 *
 * @param results - Raw search results array from API
 * @returns Normalized results with pre-parsed fields
 */
export function normalizeSearchResults(
  results: unknown[]
): NormalizedSearchResult[] {
  if (!Array.isArray(results)) return [];
  return results.map(normalizeSearchResult);
}

/**
 * Normalize a raw room type from the API.
 * Parse JSON strings for amenities and images fields.
 *
 * @param raw - Raw room type from API
 * @returns Normalized room type with pre-parsed fields
 */
export function normalizeRoomType(raw: unknown): NormalizedRoomType {
  const room = raw as Record<string, unknown>;

  // Parse amenities (may be JSON string or array)
  const rawAmenities = parseJsonField(room.amenities, []);
  const amenities = Array.isArray(rawAmenities) ? rawAmenities : [];

  // Parse images (may be JSON string or array)
  const rawImages = parseJsonField<Array<{ id: string; url: string }>>(
    room.images as string | Array<{ id: string; url: string }> | null,
    []
  );
  const images = Array.isArray(rawImages) ? rawImages : [];

  return {
    id: (room.id as string) || '',
    name: (room.name as string) || '',
    description: (room.description as string) || '',
    base_price: (room.base_price as number) || 0,
    effective_price:
      (room.effective_price as number) || (room.base_price as number) || 0,
    capacity: (room.capacity as number) || 2,
    amenities,
    images,
  };
}
